import {
  type ControllerFieldState,
  type ControllerProps,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  get,
  useFormContext,
  type UseFormStateReturn,
  useWatch,
} from "react-hook-form";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelect } from "src/system/useSelect.tsx";
import type { SelectV2Props } from "src/components/SelectV2/SelectV2.tsx";
import type {
  SingleSelectOptions,
  SingleSelectServerOptions,
} from "src/components/Form/Form.types.ts";
import { getResourceFieldOptions } from "src/system/getResourceFieldOptions.ts";
import { useTranslation } from "react-i18next";
import { useServerSelectFilter } from "src/system/useServerSelectFilter.ts";

type GetDataFn = (params: {
  signal: AbortSignal;
  depsValues: any[];
}) => Promise<{ value: string; label: string }[]>;

export type UseSingleSelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<SelectV2Props, "onChange" | "options"> &
  Omit<ControllerProps<TFieldValues, TName>, "render"> & {
    name: TName;
    forceValue?: any;
    locale?: string;
    onChange?: (value: any, form: any, resource: any) => void;
    deps?: string[];
    options?: SingleSelectOptions;
    getData?: GetDataFn;
    serverOptions?: SingleSelectServerOptions;
  };

export const useSingleSelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: UseSingleSelectFieldProps<TFieldValues, TName>,
) => {
  const {
    name,
    disabled,
    deps,
    getData,
    locale,
    options: optionsProp = [],
    onChange: onChangeProp,
    onBlur: onBlurProp,
    serverOptions,
    forceValue,
    ...restComponentProps
  } = props;
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const form = useFormContext();

  const depsValues = useWatch({
    name: deps || [],
    control: form.control,
  });

  const {
    data: backendOptions,
    refetch: refetchBackendOptions,
    isLoading: isLoadingBackendOptions,
  } = useBackendSelectOptions({
    disabled,
    depsValues,
    name,
    getData,
  });

  const filter = useServerSelectFilter({
    ...serverOptions,
  });

  const {
    options: resourceOptions,
    isLoading: isLoadingResourceOptions,
    refetch: refetchResourceList,
    getResourceByValue,
  } = useSelect({
    locale,
    resource: serverOptions ? serverOptions.resource : "",
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
    include: serverOptions?.include,
    disablePagination: true,
    filter: filter,
    sort: serverOptions?.sort,
    queryOptions: {
      enabled: !!serverOptions,
    },
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && serverOptions) {
      refetchResourceList();
    }
    if (open && getData) {
      refetchBackendOptions();
    }
  };

  const options = useMemo(() => {
    if (serverOptions) return resourceOptions;
    if (getData) return backendOptions;
    if (Array.isArray(optionsProp)) return optionsProp;

    return getResourceFieldOptions({
      resource: optionsProp.resource,
      field: optionsProp.field,
      t,
    });
  }, [serverOptions, getData, optionsProp, resourceOptions, backendOptions, t]);

  const getSelectProps = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  >({
    field: { name, value, onChange, onBlur, ref },
    formState: { errors },
  }: {
    field: ControllerRenderProps<TFieldValues, TName>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<TFieldValues>;
  }): SelectV2Props => {
    const resolvedValue = forceValue != null ? forceValue : value;

    const isLoadingData = serverOptions
      ? isLoadingResourceOptions
      : getData
      ? isLoadingBackendOptions
      : false;
    return {
      value: resolvedValue,
      name: name,
      onOpenChange: handleOpenChange,
      isLoadingData: isLoadingData,
      disabled: disabled,
      isLoadingSelectedOption: isLoadingData,
      isOpen: isOpen,
      onChange: (value) => {
        const resource = getResourceByValue(value);
        onChangeProp?.(value, form, resource);
        onChange(value);
      },

      options: options,
      onBlur: (event) => {
        onBlurProp?.(event);
        onBlur();
      },
      error: get(errors?.root || {}, name)?.message, // server error
      ...restComponentProps,
    };
  };
  return {
    getSelectProps,
  };
};

function useBackendSelectOptions({
  name,
  depsValues,
  getData,
  disabled,
}: {
  name: string;
  depsValues: any[];
  getData?: GetDataFn;
  disabled?: boolean;
}) {
  return useQuery({
    queryFn: async ({ signal }) => {
      if (!getData) return [];
      return getData({ signal, depsValues: depsValues });
    },
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [name, JSON.stringify(depsValues)],
    enabled: !disabled && !!getData,
    staleTime: 0,
    gcTime: 0,
  });
}
